import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { Check, Globe, Search, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useTranslation } from "react-i18next";

import {
  SUPPORTED_LANGUAGES,
  changeLanguage,
  type LanguageCode,
} from "../lib/i18n";
import { useLocale } from "../hooks/useLocale";

type LanguageSwitcherProps = {
  className?: string;
  /** Light chrome for dark / cinematic surfaces (e.g. Waitlist). */
  tone?: "default" | "onDark";
};

function matches(value: string, query: string): boolean {
  return value.toLowerCase().includes(query.toLowerCase());
}

export function LanguageSwitcher({
  className = "",
  tone = "default",
}: LanguageSwitcherProps) {
  const onDark = tone === "onDark";
  const { t } = useTranslation();
  const { code, language } = useLocale();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const location = useLocation();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim();
    if (!q) return SUPPORTED_LANGUAGES;
    return SUPPORTED_LANGUAGES.filter(
      (lang) =>
        matches(lang.nativeName, q) ||
        matches(lang.name, q) ||
        matches(lang.code, q),
    );
  }, [query]);

  // Close on outside click + escape.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (
        popoverRef.current?.contains(target) ||
        buttonRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        buttonRef.current?.focus();
      }
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Lock scroll while the mobile bottom sheet is open.
  useEffect(() => {
    if (!open) return;
    if (window.matchMedia("(min-width: 768px)").matches) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Autofocus search when opening.
  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => searchRef.current?.focus(), 80);
      return () => window.clearTimeout(id);
    }
    setQuery("");
    return undefined;
  }, [open]);

  function pickLanguage(nextCode: LanguageCode) {
    if (nextCode === code) {
      setOpen(false);
      return;
    }
    // Rebuild current path with the new language prefix.
    const restOfPath = location.pathname.replace(/^\/[^/]+/, "") || "";
    const nextPath = `/${nextCode}${restOfPath}${location.search}${location.hash}`;
    void changeLanguage(nextCode);
    navigate(nextPath);
    setOpen(false);
  }

  return (
    <div className={`relative ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("language_picker.label")}
        className={
          "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border text-sm font-medium transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 md:w-auto md:gap-2 md:px-3 " +
          (onDark
            ? "border-white/35 bg-white/10 text-white hover:border-white/55 hover:bg-white/18"
            : "border-border-soft bg-bg-base text-text-primary hover:border-border-strong hover:bg-bg-subtle")
        }
      >
        <Globe
          className="hidden h-[18px] w-[18px] md:inline-block"
          aria-hidden="true"
        />
        <span aria-hidden="true" className="text-base leading-none">
          {language.flag}
        </span>
        <span className="hidden uppercase tracking-wide md:inline">
          {language.code}
        </span>
      </button>

      <AnimatePresence>
        {open ? (
          <>
            {/* Mobile scrim */}
            <motion.div
              key="scrim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-[110] bg-black/40 md:hidden"
              aria-hidden="true"
            />

            <motion.div
              key="popover"
              ref={popoverRef}
              role="listbox"
              aria-label={t("language_picker.label")}
              initial={
                reduce
                  ? { opacity: 0 }
                  : { opacity: 0, y: 8, scale: 0.98 }
              }
              animate={
                reduce
                  ? { opacity: 1 }
                  : { opacity: 1, y: 0, scale: 1 }
              }
              exit={
                reduce
                  ? { opacity: 0 }
                  : { opacity: 0, y: 8, scale: 0.98 }
              }
              transition={{ duration: 0.2, ease: "easeOut" }}
              className={
                "fixed inset-x-0 bottom-0 z-[120] max-h-[80vh] overflow-hidden rounded-t-2xl border border-border-soft bg-bg-base shadow-xl " +
                "md:absolute md:inset-x-auto md:bottom-auto md:end-0 md:top-12 md:z-50 md:max-h-[28rem] md:w-[20rem] md:rounded-2xl"
              }
            >
              <div className="flex items-center justify-between gap-3 border-b border-border-soft px-4 py-3 md:hidden">
                <h2 className="text-base font-semibold text-text-primary">
                  {t("language_picker.label")}
                </h2>
                <button
                  type="button"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-border-soft text-text-primary"
                  aria-label={t("language_picker.close")}
                  onClick={() => setOpen(false)}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>

              <div className="border-b border-border-soft px-3 py-3">
                <div className="flex items-center gap-2 rounded-full bg-bg-subtle px-3 py-2">
                  <Search
                    className="h-4 w-4 text-text-subtle"
                    aria-hidden="true"
                  />
                  <input
                    ref={searchRef}
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("language_picker.search_placeholder")}
                    className="w-full bg-transparent text-sm text-text-primary outline-none placeholder:text-text-subtle"
                    aria-label={t("language_picker.search_placeholder")}
                  />
                </div>
              </div>

              <ul className="max-h-[60vh] overflow-y-auto py-2 md:max-h-[20rem]">
                {filtered.length === 0 ? (
                  <li className="px-4 py-6 text-center text-sm text-text-subtle">
                    {t("language_picker.no_results", { query })}
                  </li>
                ) : (
                  filtered.map((lang) => {
                    const active = lang.code === code;
                    return (
                      <li key={lang.code}>
                        <button
                          type="button"
                          role="option"
                          aria-selected={active}
                          onClick={() => pickLanguage(lang.code)}
                          className={
                            "flex w-full items-center gap-3 px-4 py-2.5 text-start transition-colors duration-150 " +
                            (active
                              ? "bg-brand-50 text-brand-500 dark:bg-brand-950"
                              : "text-text-primary hover:bg-bg-subtle")
                          }
                          dir={lang.rtl ? "rtl" : "ltr"}
                        >
                          <span
                            aria-hidden="true"
                            className="text-xl leading-none"
                          >
                            {lang.flag}
                          </span>
                          <span className="flex-1">
                            <span className="block text-sm font-semibold">
                              {lang.nativeName}
                            </span>
                            <span
                              className={
                                "block text-xs " +
                                (active
                                  ? "text-brand-500/80"
                                  : "text-text-subtle")
                              }
                            >
                              {lang.name}
                            </span>
                          </span>
                          {active ? (
                            <Check
                              className="h-4 w-4 text-brand-500"
                              aria-hidden="true"
                              strokeWidth={3}
                            />
                          ) : null}
                        </button>
                      </li>
                    );
                  })
                )}
              </ul>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
